import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import './style.scss';
import { Portal } from '@material-ui/core';

// ODB coverage for a DIN doesn't change within a session, but this component
// renders in several places at once (the drug card, every Medications Prescribed
// row) and remounts on each list re-render. getOdbDrugByDin is an uncached thunk,
// so without this each mount refetched the same DIN. Cache the result per DIN and
// share a single in-flight request across all instances.
const odbCache = new Map();
const odbInflight = new Map();

function fetchOdbByDin(dispatch, din) {
  if (odbCache.has(din)) return Promise.resolve(odbCache.get(din));
  if (odbInflight.has(din)) return odbInflight.get(din);

  const request = dispatch(api.Drugs.getOdbDrugByDin({ din }))
    .then((response) => {
      const result = response ?? null;
      odbCache.set(din, result);
      return result;
    })
    .finally(() => {
      odbInflight.delete(din);
    });

  odbInflight.set(din, request);
  return request;
}

const OdbCoverageInfo = ({ din, placement = 'bottom', onLuCodeClick }) => {
  const dispatch = useAppDispatch();
  const [odb, setOdb] = useState(() => (din && odbCache.has(din) ? odbCache.get(din) : null));
  const [loading, setLoading] = useState(false);
  // Stable per-instance id so multiple instances for the same DIN don't collide
  // on tooltip anchor ids.
  const [luTooltipId] = useState(() => `${Math.random()}`);

  useEffect(() => {
    if (!din) return undefined;

    if (odbCache.has(din)) {
      setOdb(odbCache.get(din));
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    fetchOdbByDin(dispatch, din)
      .then((response) => {
        if (!cancelled) setOdb(response);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, din]);

  const getCoverageStatusDisplay = (status) => {
    if (!status) return null;
    const statusMap = {
      Covered: { label: 'ODB', className: 'covered' },
      LimitedUse: { label: 'ODB-LU', className: 'limited-use' },
      NotCovered: { label: 'ODB ✗', className: 'not-covered' },
    };
    const statusInfo = statusMap[status] || { label: 'ODB', className: 'unknown' };
    return <div className={`odb-coverage-status ${statusInfo.className}`}>{statusInfo.label}</div>;
  };

  // Render nothing until there's something to show — no loading placeholder, so
  // the coverage info just pops in when data is available (and the card's
  // divider stays hidden when there's no ODB data at all).
  if (loading || !odb?.drug) {
    return null;
  }

  const luCriteria = odb.lu_criteria?.[0]; // Only one LU criteria per drug

  // Parse criteria notes to group LU codes with their authorization periods
  const parsedCriteria = [];
  if (luCriteria?.criteria_notes) {
    for (let i = 0; i < luCriteria.criteria_notes.length; i++) {
      const note = luCriteria.criteria_notes[i];

      // Skip if no reason_for_use_id (not an actual LU code)
      if (!note.reason_for_use_id) {
        continue;
      }

      // Look ahead for authorization period (note_type "R")
      let authPeriod = null;
      if (i + 1 < luCriteria.criteria_notes.length) {
        const nextNote = luCriteria.criteria_notes[i + 1];
        if (nextNote.note_type?.includes('R')) {
          authPeriod = nextNote.text;
        }
      }

      parsedCriteria.push({
        reasonForUseId: note.reason_for_use_id,
        text: note.text,
        authorizationPeriod: authPeriod,
      });
    }
  }

  // Create tooltip ID for the coverage status badge
  const statusTooltipId = `odb-status-${din}-${luTooltipId}`;

  return (
    <div className="odb-coverage-info">
      <div id={statusTooltipId} className="odb-status-wrapper">
        {getCoverageStatusDisplay(odb.drug.coverage_status)}
      </div>
      {odb.drug.drug_benefit_price > 0 && (
        <span className="odb-price">
          <span className="odb-price-key">Price</span>
          <span className="odb-price-val">${odb.drug.drug_benefit_price.toFixed(2)}</span>
        </span>
      )}
      <Portal>
        <ReactTooltip
          anchorId={statusTooltipId}
          place={placement}
          className="careand-tooltip odb-tooltip"
          positionStrategy="fixed"
          style={{ fontSize: '13px' }}
        >
          <div>
            <div className="lu-tooltip-header">
              <div className="lu-tooltip-drug-info">
                {odb.drug.product_name && (
                  <span className="lu-tooltip-product">{odb.drug.product_name}</span>
                )}
              </div>
              <div className="lu-tooltip-meta">
                {(odb.drug.strength || odb.drug.dosage_form) && (
                  <span className="lu-tooltip-strength">
                    {[odb.drug.strength, odb.drug.dosage_form].filter(Boolean).join(' ')}
                    {odb.drug.manufacturer_name && `, ${odb.drug.manufacturer_name}`}
                  </span>
                )}
              </div>
            </div>
            <div className="lu-tooltip-body">
              <div style={{ marginBottom: '6px' }}>
                <strong>Coverage:</strong>{' '}
                {odb.drug.coverage_status === 'Covered'
                  ? 'Covered'
                  : odb.drug.coverage_status === 'LimitedUse'
                  ? 'Limited Use'
                  : odb.drug.coverage_status === 'NotCovered'
                  ? 'Not Covered'
                  : odb.drug.coverage_status}
              </div>
              {odb.drug.drug_benefit_price > 0 && (
                <div>
                  <strong>ODB Price:</strong> ${odb.drug.drug_benefit_price.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </ReactTooltip>
      </Portal>
      {parsedCriteria.map((criteria, idx) => {
        const tooltipId = `lu-note-${luCriteria.lcc_id}-${criteria.reasonForUseId}-${din}-${luTooltipId}`;

        return (
          <React.Fragment key={`lu-badge-${criteria.reasonForUseId}-${idx}`}>
            <div
              id={tooltipId}
              className="lu-badge"
              onClick={(e) => {
                e.stopPropagation();
                if (onLuCodeClick) {
                  onLuCodeClick(criteria.reasonForUseId, criteria.text);
                }
              }}
            >
              LU {criteria.reasonForUseId}
            </div>
            <Portal>
              <ReactTooltip
                anchorId={tooltipId}
                place={placement}
                className="careand-tooltip odb-tooltip"
                positionStrategy="fixed"
                style={{ fontSize: '13px' }}
              >
                <div>
                  <div className="lu-tooltip-header">
                    <div className="lu-tooltip-drug-info">
                      {odb.drug.product_name && (
                        <span className="lu-tooltip-product">
                          {odb.drug.product_name}{' '}
                          {luCriteria.generic_name && `(${luCriteria.generic_name})`}
                        </span>
                      )}
                    </div>
                    <div className="lu-tooltip-meta">
                      {(odb.drug.strength || odb.drug.dosage_form) && (
                        <span className="lu-tooltip-strength">
                          {[odb.drug.strength, odb.drug.dosage_form].filter(Boolean).join(' ')}
                          {odb.drug.manufacturer_name && `, ${odb.drug.manufacturer_name}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="lu-tooltip-body">
                    {criteria.authorizationPeriod && (
                      <div className="lu-tooltip-auth">{criteria.authorizationPeriod}</div>
                    )}
                    <div className="lu-tooltip-criteria">
                      <strong>Criteria:</strong> {criteria.text}
                    </div>
                  </div>
                </div>
              </ReactTooltip>
            </Portal>
          </React.Fragment>
        );
      })}
    </div>
  );
};

OdbCoverageInfo.propTypes = {
  din: PropTypes.string.isRequired,
  placement: PropTypes.string,
  onLuCodeClick: PropTypes.func,
};

export default OdbCoverageInfo;
